import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useInvoicePreview, useSetLineItemItemized } from '@/hooks/useInvoicePreview';

export function handleItemizeMessage(
  event: MessageEvent,
  iframe: HTMLIFrameElement | null,
  onToggle: (v: { lineItemId: string; itemized: boolean }) => void,
) {
  if (!iframe || event.source !== iframe.contentWindow) return;
  const data = event.data;
  if (!data || data.type !== 'jobos:itemize-toggle') return;
  onToggle({ lineItemId: String(data.lineItemId), itemized: Boolean(data.itemized) });
}

export function InvoicePreview({ jobId }: { jobId: string }) {
  const { data, isLoading } = useInvoicePreview(jobId);
  const setItemized = useSetLineItemItemized(jobId, data?.documentId ?? undefined);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const listener = (e: MessageEvent) =>
      handleItemizeMessage(e, iframeRef.current, (v) => setItemized.mutate(v));
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [setItemized]);

  if (isLoading) {
    return <div className="py-12 text-center text-gray-400">Loading preview…</div>;
  }

  // Cover the iframe only while a toggle's PATCH is in flight, to give feedback
  // and stop rapid double-clicks. No preview refetch happens on toggle (the
  // local hide/show is authoritative), so this is brief.
  const busy = setItemized.isPending;

  return (
    <div className="relative">
      <iframe
        ref={iframeRef}
        title="Invoice preview"
        sandbox="allow-scripts"
        srcDoc={data?.html ?? ''}
        className="w-full h-[78vh] border rounded-lg bg-white"
      />
      {busy && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-white/40">
          <Loader2 className="h-8 w-8 animate-spin text-blue-900" />
          <span className="text-sm font-medium text-gray-600">Saving…</span>
        </div>
      )}
    </div>
  );
}
